import React from 'react';
import { Button, Textarea, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { Dismiss12Filled, SquareFilled } from '@fluentui/react-icons';
import { ReactComponent as Arrow } from '../../../assets/arrow-smallupward-icon-black-rgb.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { dismissDisclaimer } from '../../../redux/features/app/appSlice';
import { Breakpoints, customColors, customFonts } from '../../../styles';
import useChatInput from '../hooks/useChatInput';
import UploadMenu from './UploadMenu';
import ToolkitTray from './ToolkitTray';
import ToolkitButton from './ToolkitButton';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '80%',
        width: '85%',
        maxWidth: '85%',
        ...shorthands.margin('auto'),
        ...shorthands.padding('8px', '0', '0', '0'),
    },
    rootEmptyState: {
        minWidth: '95%',
        width: '100%',
        maxWidth: '100%',
    },
    promptContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        marginBottom: '8px',
        transform: 'translateY(16px)',
        ...shorthands.transition('transform', '0.2s', 'ease'),
    },
    promptContainerToolkitOpen: {
        transform: 'translateY(0)',
    },
    promptContainerStatic: {
        transform: 'translateY(0)',
    },

    inputColumn: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0,
    },
    toolkitTraySlot: {
        marginTop: '6px',
        overflow: 'visible',
        position: 'relative',
        zIndex: 2,
    },

    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: '30px',
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        backgroundColor: customColors.white,
        transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    },
    inputContainerGenerating: {
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        '::before': {
            content: '""',
            position: 'absolute',
            top: '1px',
            left: 0,
            width: '50%',
            height: '4px',
            background: 'linear-gradient(to right, transparent, #0069ab 15%, #F6871F 40%, #fd881b 85%, transparent)',
            boxShadow: '0 6px 6px 0px rgba(239, 126, 19, 0.7)',
            animationName: {
                '0%': { transform: 'translateX(-100%)', opacity: 1 },
                '50%': { transform: 'translateX(30%)', opacity: 1 },
                '70%': { transform: 'translateX(70%)', opacity: 0.5 },
                '100%': { transform: 'translateX(-100%)', opacity: 0 },
            },
            animationDuration: '2.2s',
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
        },
    },
    inputRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        width: '100%',
        '& > span:hover, & > span:active, & > span:after, & > span:focus, & > span:focus-within': {
            ...shorthands.border('0px'),
        },
    },
    input: {
        width: '100%',
        fontSize: '2em',
        ...shorthands.border('0px'),
    },
    textarea: {
        maxHeight: '150px',
        minHeight: '10px',
        minWidth: '200px',
        lineHeight: '1em',
        overflowY: 'auto',
        resize: 'none',
        ...shorthands.border('none'),
        fontFamily: customFonts.Lato,
        ...shorthands.padding('5px'),
        alignContent: 'center',
        '&::placeholder': {
            fontSize: '1em',
        },
        '&::-webkit-input-placeholder': {
            fontSize: '1em',
        },
        '&::-moz-placeholder': {
            fontSize: '1em',
        },
        '&:-ms-input-placeholder': {
            fontSize: '1em',
        },
        '::-webkit-resizer': {
            display: 'none',
        },
        '::-webkit-scrollbar': {
            width: '0rem !important',
        },
        ...shorthands.borderRadius('5px'),
    },
    sendButton: {
        justifySelf: 'center',
        ...shorthands.margin('5px'),
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        maxHeight: '120px',
        ...shorthands.transition('all', '0.3s', 'ease'),
        '& > span > svg': {
            fill: customColors.protivitiDarkBlue,
            stroke: customColors.protivitiDarkBlue,
            ...shorthands.transition('all', '0.3s', 'ease'),
        },
        '&:hover:enabled': {
            backgroundColor: customColors.protivitiDarkBlue,
            ...shorthands.borderColor(customColors.protivitiOrange),
            '& > span > svg': {
                fill: customColors.white,
                stroke: customColors.white,
            },
        },
        '&:active:enabled': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.transition('all', '0s'),
        },
        '&:disabled, &:disabled:hover, &:disabled:active, &:disabled:focus, &:disabled:focus-within': {
            ...shorthands.border('1px', 'solid', 'lightgray'),
            backgroundColor: customColors.white,
            cursor: 'not-allowed',
            '& > span > svg': {
                fill: 'lightgray',
                stroke: 'lightgray',
            },
        },
    },
    sendButtonFocused: {
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
    },
    imagePreviewWrapper: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid ' + customColors.botMessageBorder,
    },
    imagePreviewStrip: {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: '8px',
        padding: '8px 8px 6px 12px',
        overflowX: 'auto',
        flex: 1,
        '::-webkit-scrollbar': {
            height: '4px',
        },
        '::-webkit-scrollbar-thumb': {
            borderRadius: '2px',
            backgroundColor: customColors.botMessageBorder,
        },
    },
    imagePreviewCount: {
        flexShrink: 0,
        padding: '0 10px',
        fontSize: '11px',
        color: 'rgba(0, 0, 0, 0.45)',
        fontFamily: customFonts.Lato,
        whiteSpace: 'nowrap',
    },
    imagePreviewItem: {
        position: 'relative',
        width: '72px',
        height: '72px',
        flexShrink: 0,
        borderRadius: '8px',
        overflow: 'hidden',
    },
    imagePreviewThumb: {
        width: '72px',
        height: '72px',
        objectFit: 'cover',
        borderRadius: '8px',
        display: 'block',
    },
    imagePreviewRemove: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        minWidth: '18px',
        width: '18px',
        height: '18px',
        padding: '0',
        borderRadius: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
        color: customColors.white,
        fontSize: '12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        lineHeight: '1',
        ':hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
    },
    stopButton: {
        justifySelf: 'center',
        ...shorthands.margin('5px'),
        ...shorthands.border('1px', 'solid', customColors.protivitiOrange),
        ...shorthands.borderRadius('5px'),
        maxHeight: '120px',
        color: customColors.protivitiDarkBlue,
        ...shorthands.transition('all', '0.3s', 'ease'),
        '& svg': {
            fill: customColors.protivitiDarkBlue,
            ...shorthands.transition('all', '0.3s', 'ease'),
        },
        '&:hover': {
            backgroundColor: customColors.protivitiOrange,
            ...shorthands.borderColor(customColors.protivitiDarkBlue),
            '& svg': {
                fill: customColors.white,
            },
        },
        '&:active': {
            backgroundColor: customColors.protivitiDarkBlue,
            ...shorthands.transition('all', '0s'),
        },
    },
    disclaimer: {
        display: 'flex',
        alignItems: 'flex-start',
        marginTop: '4px',
        color: tokens.colorNeutralForeground2,
        '& .disclaimerDismiss': { opacity: 0 },
        '&:hover .disclaimerDismiss': { opacity: 1 },
    },
    disclaimerBody: {
        flex: '1',
        fontFamily: customFonts.Lato,
        fontSize: '12px',
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        lineHeight: '1.4',
        ...shorthands.padding('4px', '0'),
        ...Breakpoints.medium({ fontSize: '11px' }),
        ...Breakpoints.small({ fontSize: '10px' }),
    },

});

interface ChatInputProps {
    prefillText?: string;
    prefillVersion?: number;
    layoutVariant?: 'default' | 'empty-state';
}


const ChatInput: React.FC<ChatInputProps> = ({ prefillText, prefillVersion, layoutVariant = 'default' }) => {
    const styles = useStyles();
    const vm = useChatInput();
    const dispatch = useAppDispatch();
    const [isFocused, setIsFocused] = React.useState(false);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const selectedSessionId = useAppSelector((state) => state.chatUi.selectedSessionId);
    const disclaimerDismissed = useAppSelector((state) => state.app.disclaimerDismissed);
   
    React.useEffect(() => {
        if (prefillText && prefillText.trim().length > 0) {
            vm.onDraftInputChange(prefillText);
        }
    }, [prefillText, prefillVersion, vm.onDraftInputChange]);

    // focus text area when session is selected
    React.useEffect(() => {
        if (!selectedSessionId || vm.isTextAreaDisabled) {
            return;
        }

        textAreaRef.current?.focus();
    }, [selectedSessionId, vm.isTextAreaDisabled]);

    // Auto-resize textarea height to content, up to CSS maxHeight
    React.useEffect(() => {
        const el = textAreaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [vm.draftInput]);

    const rootClassName =
        layoutVariant === 'empty-state' ? mergeClasses(styles.root, styles.rootEmptyState) : styles.root;

    return (
        <div className={rootClassName}>
            <div
                className={mergeClasses(
                    styles.promptContainer,
                    layoutVariant === 'empty-state' ? styles.promptContainerStatic : undefined,
                    layoutVariant !== 'empty-state' && vm.isToolkitOpen ? styles.promptContainerToolkitOpen : undefined,
                )}
            >
                {(vm.capabilities?.supportsBulkPromptUpload || vm.capabilities?.supportsDocumentUpload || !!vm.resolvedSessionAgent) &&
                    <UploadMenu
                        isBulkPromptProcessing={vm.isBulkPromptProcessing}
                        isDocumentUploading={vm.isDocumentUploading}
                        resolvedSessionAgent={vm.resolvedSessionAgent}
                        onBulkPromptUpload={vm.onBulkPromptUpload}
                        onDocumentUpload={vm.onDocumentUpload}
                        onImageUpload={vm.resolvedSessionAgent ? vm.addImage : undefined}
                    />
                }

                {vm.canShowToolkitButton && <ToolkitButton isOpen={vm.isToolkitOpen} onToggle={vm.onToggleToolkit} />}

                <div className={styles.inputColumn}>
                    <div
                        className={mergeClasses(
                            styles.inputContainer,
                            vm.isResponseGenerating ? styles.inputContainerGenerating : undefined,
                        )}
                    >
                        {vm.pendingImages.length > 0 && (
                            <div className={styles.imagePreviewWrapper}>
                                <div className={styles.imagePreviewStrip}>
                                    {vm.pendingImages.map(image => (
                                        <div key={image.id} className={styles.imagePreviewItem}>
                                            <img src={image.previewUrl} alt="pending" className={styles.imagePreviewThumb} />
                                            <button
                                                type="button"
                                                className={styles.imagePreviewRemove}
                                                aria-label="Remove image"
                                                onClick={() => vm.removeImage(image.id)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <span className={styles.imagePreviewCount}>{vm.pendingImages.length}/3</span>
                            </div>
                        )}
                        <div className={styles.inputRow}>
                            <Textarea
                                className={styles.input}
                                textarea={{ className: styles.textarea, ref: textAreaRef }}
                                placeholder="Send a message"
                                value={vm.draftInput}
                                disabled={vm.isTextAreaDisabled}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                onChange={(event) => vm.onDraftInputChange(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && !event.shiftKey) {
                                        event.preventDefault();
                                        if (!vm.isBusy) {
                                            void vm.onSubmit();
                                        }
                                    }
                                }}
                            />
                            {vm.isResponseGenerating ? (
                                <Button
                                    aria-label="stop_answering"
                                    title="Stop answering"
                                    appearance="transparent"
                                    className={styles.stopButton}
                                    icon={<SquareFilled />}
                                    onClick={vm.onAbort}
                                />
                            ) : (
                                <Button
                                    aria-label="submit_prompt"
                                    appearance="transparent"
                                    className={
                                        isFocused
                                            ? mergeClasses(styles.sendButton, styles.sendButtonFocused)
                                            : styles.sendButton
                                    }
                                    icon={<Arrow />}
                                    onClick={() => {
                                        void vm.onSubmit();
                                    }}
                                    disabled={vm.isBusy || (vm.draftInput.trim().length === 0 && vm.pendingImages.length === 0)}
                                />
                            )}
                        </div>
                    </div>
                    {vm.canShowToolkitButton && vm.isToolkitOpen && (
                        <div className={styles.toolkitTraySlot}>
                            <ToolkitTray
                                onPromptSelect={vm.onDraftInputChange}
                                canEnhancePrompt={vm.canEnhancePrompt}
                                canUndoPromptEnhancement={vm.canUndoPromptEnhancement}
                                isPromptEnhancing={vm.isPromptEnhancing}
                                onEnhancePrompt={() => {
                                    void vm.onEnhancePrompt();
                                }}
                                onUndoPromptEnhancement={vm.onUndoPromptEnhancement}
                            />
                        </div>
                    )}
                </div>
            </div>
            {!disclaimerDismissed && (
                <div className={styles.disclaimer}>
                    <span className={styles.disclaimerBody}>
                        You must follow{' '}
                        <a
                            href="https://roberthalf.sharepoint.com/:b:/r/sites/iShare-GenAI/Shared%20Documents/ProGPT%20Guidelines.pdf?csf=1&web=1&e=svAzdD"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            these requirements
                        </a>{' '}
                        when using ProGPT. You must always review, revise, and verify ProGPT output and all
                        references/sources for relevance, creditability and accuracy before you use it in
                        deliverables or thought leadership. Please note data input into ProGPT may be processed
                        in the US.
                    </span>
                    <Button
                        className="disclaimerDismiss"
                        appearance="transparent"
                        size="small"
                        icon={<Dismiss12Filled aria-label="dismiss message" />}
                        onClick={() => { dispatch(dismissDisclaimer()); }}
                    />
                </div>
            )}
        </div>
    );
};

export default ChatInput;
